import { useState } from "react";
import { Box, Typography, TextField, Paper, Button } from "@mui/material";
import {
  locationServicesField,
  locationServicesPaperBox,
  uploadFileButtonStyle,
  removeFileButtonStyle,
  visitPriceFieldStyle,
  additionalStepTitleStyle,
  visitPriceFlexStyle,
  additionalStepDescriptionStyle,
  visitPriceFieldBoxStyle,
  optionalStyle,
  experienceDetailsWrapperStyles,
  filesWrapperStyles,
  singleFileBoxStyles,
  singleFileLinkStyle,
  currencySymbolStyle,
} from "../styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../../Components/SnackBar";
import { getCurrencySymbol } from "../../../../../Utils/CountriesData/getCountriesData";
import CustomSwitch from "../../../../../Components/CustomSwitch";

const MAX_PENDING_FILES = 5;
const PricelistAdditionalInformation = ({
  formik,
  addPendingFile,
  removePendingFile,
  addFileToDelete,
  pendingFiles,
  requiredFilesText,
  hasAnyVariantPriced,
}) => {
  const { t } = useTranslation("price-services-steps");
  const snackbar = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const [visitPriceTouched, setVisitPriceTouched] = useState(false);

  const {
    values: {
      pricelist: {
        currency,
        visit_price,
        visit_price_enabled,
        visit_price_relevant,
        supplier_files_mandatory,
        supplier_experience_mandatory,
      },
    },
  } = formik;

  const currencySymbol = getCurrencySymbol(currency);

  const handleVisitPriceChange = (e) => {
    const { value } = e.target;
    formik.setFieldValue(
      "pricelist.visit_price",
      value === "" ? "" : parseFloat(value)
    );
  };

  const handleExperienceChange = (e) => {
    formik.setFieldValue("pricelist.years_of_experience", e.target.value);
  };

  const handleDetailsChange = (e) => {
    formik.setFieldValue(
      "pricelist.additional_info.additional_details",
      e.target.value
    );
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check pending files limit
    const newPendingFilesCount = pendingFiles.length + files.length;
    if (newPendingFilesCount > MAX_PENDING_FILES) {
      snackbar(
        `${t("max_pending_files_error")} ${MAX_PENDING_FILES} ${t(
          "max_pending_files_error_2"
        )}`,
        "error"
      );
      return;
    }

    setIsUploading(true);

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/webp",
      "image/jpeg",
      "image/png",
      "video/mp4",
      "video/quicktime",
      "video/webm",
      "video/x-msvideo",
    ];
    const maxFileSize = 25 * 1024 * 1024;

    const invalidSizeFiles = [];
    const invalidTypeFiles = [];

    try {
      files.forEach((file) => {
        const isSizeValid = file.size <= maxFileSize;
        const isTypeValid = allowedTypes.includes(file.type);

        if (!isSizeValid) invalidSizeFiles.push(file.name);
        if (!isTypeValid) invalidTypeFiles.push(file.name);

        if (isSizeValid && isTypeValid) {
          const previewFile = {
            name: file.name,
            size: file.size,
            type: file.type,
            preview: URL.createObjectURL(file),
            rawFile: file,
            isPending: true,
          };
          addPendingFile(previewFile);
        }
      });

      const sizeError = invalidSizeFiles.length > 0;
      const typeError = invalidTypeFiles.length > 0;

      if (sizeError && typeError) {
        snackbar(t("files_error_size_and_type"), "error");
      } else if (sizeError) {
        snackbar(t("files_error_size"), "error");
      } else if (typeError) {
        snackbar(t("files_error_type"), "error");
      }
    } catch (error) {
      console.error("Error processing files:", error);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleFileRemove = async (fileToRemove, index) => {
    try {
      if (fileToRemove.isPending) {
        removePendingFile(fileToRemove.pendingId);
      } else {
        addFileToDelete(fileToRemove.url);
        const updatedFiles =
          formik.values.pricelist.additional_info.files.filter(
            (file, i) => i !== index
          );
        formik.setFieldValue("pricelist.additional_info.files", updatedFiles);
      }
    } catch (error) {
      console.error("Error removing file:", error);
    }
  };

  const allFiles = [
    ...formik.values.pricelist.additional_info.files.map((file) => ({
      ...file,
      isPending: false,
    })),
    ...pendingFiles?.map((file) => ({
      ...file,
      url: file.preview,
      isPending: true,
    })),
  ];

  // Disable upload button if pendingFiles length is 5 or more
  const isUploadDisabled = isUploading || pendingFiles.length >= 5;

  const handleVisitPriceEnabledChange = (e) => {
    const isEnabled = e.target.checked;

    formik.setFieldValue("pricelist.visit_price_enabled", isEnabled);

    if (!isEnabled) {
      formik.setFieldValue("pricelist.visit_price", "");
    }
  };

  const isVisitPriceEmpty =
    visit_price === "" ||
    visit_price === null ||
    typeof visit_price === "undefined" ||
    Number.isNaN(visit_price);

  const showVisitPriceError =
    visit_price_enabled && isVisitPriceEmpty && visitPriceTouched;

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Box mb="8px">
        <Typography fontSize="19px" fontWeight={500}>
          {t("additional_info")}
        </Typography>
        <Typography sx={additionalStepDescriptionStyle}>
          {t("additional_info_description")}
        </Typography>
      </Box>

      {visit_price_relevant ? (
        <Paper
          variant="outlined"
          sx={{ ...locationServicesPaperBox, gap: "8px" }}
        >
          <Box sx={visitPriceFlexStyle}>
            <Typography sx={additionalStepTitleStyle}>
              {(visit_price_enabled ? "* " : "") + t("visit_price")}
            </Typography>

            {hasAnyVariantPriced && (
              <CustomSwitch
                checked={visit_price_enabled}
                onChange={handleVisitPriceEnabledChange}
                switchTrackStyles={{
                  backgroundColor: "rgba(165, 165, 165, 1)",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              ...visitPriceFlexStyle,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box>
              <Typography sx={additionalStepDescriptionStyle}>
                {t("price_for_visit")}
              </Typography>

              {!visit_price_enabled ? (
                <Box display="flex" gap="6px" alignItems="center" mt="6px">
                  <img src="/icons/alert-error-circle.svg" alt="alert" />
                  <Typography
                    sx={{
                      ...additionalStepDescriptionStyle,
                      color: "rgba(183, 72, 74, 1)",
                    }}
                  >
                    {t("visit_price_off_alert")}
                  </Typography>
                </Box>
              ) : null}

            </Box>

            <Box sx={visitPriceFieldBoxStyle(!visit_price_enabled)}>
              <Typography sx={currencySymbolStyle}>{currencySymbol}</Typography>
              <TextField
                type="text"
                value={visit_price?.toString().replace(/[£$€₪]/g, "") || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value) || value === "") {
                    handleVisitPriceChange(e);
                  }
                }}
                onBlur={() => setVisitPriceTouched(true)}
                variant="outlined"
                sx={visitPriceFieldStyle}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                error={showVisitPriceError}
                helperText={
                  showVisitPriceError ? t("visit_price_empty_error") : ""
                }
              />
            </Box>
          </Box>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={locationServicesPaperBox}>
        <Typography sx={additionalStepTitleStyle}>
          {supplier_files_mandatory && "* "}
          {t("upload_files")}{" "}
          {!supplier_files_mandatory && (
            <Box component="span" sx={optionalStyle}>
              ({t("optional")})
            </Box>
          )}
        </Typography>

        {/* <Typography sx={additionalStepDescriptionStyle}>
          {t("allowed_files_types")}
        </Typography> */}

        {requiredFilesText &&
        formik.values.pricelist.supplier_files_mandatory ? (
          <Typography sx={additionalStepDescriptionStyle}>
            {t("required_documents")}: {t("required_files_text")}
          </Typography>
        ) : null}

        <Box sx={filesWrapperStyles}>
          {allFiles.map((file, index) => (
            <Box
              key={
                file.isPending
                  ? `pending-${file.pendingId}`
                  : `file-${file.url}`
              }
              display="flex"
              alignItems="center"
            >
              <Box sx={singleFileBoxStyles}>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={singleFileLinkStyle}
                >
                  <img
                    src="/upload-icon.svg"
                    alt="Upload Icon"
                    style={{ width: "16px", height: "16px" }}
                  />
                  {file.name}
                </a>
                <Box
                  onClick={() => handleFileRemove(file, index)}
                  sx={removeFileButtonStyle}
                >
                  <img
                    src="/cancel_icon.svg"
                    alt="Remove Icon"
                    style={{ width: "12px", height: "12px" }}
                  />
                </Box>
              </Box>
            </Box>
          ))}

          <Button
            variant="contained"
            component="label"
            disabled={isUploadDisabled}
            sx={uploadFileButtonStyle}
          >
            {isUploading ? t("uploading") : t("upload_file_button")}
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload}
              disabled={isUploadDisabled}
              accept=".pdf,.doc,.docx,.webp,.jpeg,.png,.mp4,.mov,.webm,.avi"
            />
          </Button>
        </Box>
      </Paper>

      <Box sx={experienceDetailsWrapperStyles}>
        <Paper
          variant="outlined"
          sx={{
            ...locationServicesPaperBox,
            flex: 1,
            alignSelf: { xs: "none", md: "flex-start" },
          }}
        >
          <Typography sx={additionalStepTitleStyle}>
            {supplier_experience_mandatory && "* "}
            {t("years_of_experience")}{" "}
            {!supplier_experience_mandatory && (
              <Box component="span" sx={optionalStyle}>
                ({t("optional")})
              </Box>
            )}
          </Typography>

          <TextField
            fullWidth
            type="text"
            required={formik.values.pricelist.supplier_experience_mandatory}
            value={formik.values.pricelist.years_of_experience}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                handleExperienceChange(e);
              }
            }}
            placeholder={t("years_of_experience_placeholder")}
            sx={locationServicesField}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
        </Paper>

        <Paper variant="outlined" sx={{ ...locationServicesPaperBox, flex: 1 }}>
          <Typography sx={additionalStepTitleStyle}>
            {t("additional_details")}{" "}
            <Box component="span" sx={optionalStyle}>
              ({t("optional")})
            </Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            value={formik.values.pricelist.additional_info.additional_details}
            onChange={handleDetailsChange}
            placeholder={t("additional_details_placeholder")}
            sx={locationServicesField}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default PricelistAdditionalInformation;
